import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { CirclePlus, Eye, RefreshCw, SquarePen, Trash2, Warehouse } from 'lucide-react';
import Layout from '../../components/Layout';
import DataGrid, { GridColumn, GridAction } from '../../components/DataGrid/DataGrid';
import PermissionGuard from '../../components/PermissionGuard';
import { useAuth } from '../../context/AuthContext';
import { Permissions } from '../../constants/Permissions';
import warehouseService from '../../services/warehouseService';
import './WarehouseListPage.css';

const WarehouseListPage: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { t } = useTranslation();
  const { hasPermission } = useAuth();

  const [items,          setItems]          = useState<any[]>([]);
  const [loading,        setLoading]        = useState(true);
  const [refreshing,     setRefreshing]     = useState(false);
  const [error,          setError]          = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(
    (location.state as { message?: string })?.message ?? null
  );

  const [deleteId,        setDeleteId]        = useState<number | null>(null);
  const [deleteName,      setDeleteName]      = useState<string>('');
  const [deleting,        setDeleting]        = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);

  const loadItems = useCallback(async (isRefresh = false) => {
    if (isRefresh) setRefreshing(true); else setLoading(true);
    setError(null);
    try {
      const res = await warehouseService.getWarehouses(1, 100);
      setItems(res?.data?.data ?? []);
    } catch {
      setError(t('warehouse.failedToLoadWarehouse'));
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, [t]);

  useEffect(() => { loadItems(); }, [loadItems]);

  useEffect(() => {
    if (!successMessage) return;
    const timer = setTimeout(() => setSuccessMessage(null), 4000);
    return () => clearTimeout(timer);
  }, [successMessage]);

  // ── Stats ────────────────────────────────────────────────────────────────

  const totalWarehouses  = items.length;
  const activeWarehouses = items.filter(w => w.isActive).length;
  const uniqueCities     = new Set(items.map(w => w.city).filter(Boolean)).size;

  // ── Delete ───────────────────────────────────────────────────────────────

  const openDeleteModal  = (id: number, name: string) => { setDeleteId(id); setDeleteName(name); setShowDeleteModal(true); };
  const closeDeleteModal = () => { setDeleteId(null); setDeleteName(''); setShowDeleteModal(false); };

  const handleDelete = async () => {
    if (!deleteId) return;
    setDeleting(true);
    try {
      await warehouseService.deleteWarehouse(deleteId);
      setSuccessMessage(t('warehouse.deleteSuccess'));
      closeDeleteModal();
      await loadItems();
    } catch {
      setError(t('warehouse.failedToDeleteWarehouse'));
      closeDeleteModal();
    } finally {
      setDeleting(false);
    }
  };

  // ── Grid ─────────────────────────────────────────────────────────────────

  const columns: GridColumn[] = [
    {
      key: 'name',
      label: t('warehouse.warehouseName'),
      sortable: true,
      width: '220px',
      render: (value) => <strong>{value}</strong>,
    },
    {
      key: 'code',
      label: t('warehouse.code'),
      sortable: true,
      width: '120px',
      render: (value) => <span className="wh-code-pill">{value}</span>,
    },
    {
      key: 'location',
      label: t('warehouse.location'),
      sortable: true,
      width: '200px',
    },
    {
      key: 'city',
      label: t('warehouse.city'),
      sortable: true,
      width: '150px',
      hideTablet: true,
      hideMobile: true,
    },
    {
      key: 'isActive',
      label: t('common.status'),
      width: '110px',
      render: (value) => (
        <span className={`ims-badge ${value ? 'ims-badge-approved' : 'ims-badge-closed'}`}>
          {value ? t('common.active') : t('common.inactive')}
        </span>
      ),
    },
    {
      key: 'createdAt',
      label: t('common.createdDate'),
      sortable: true,
      width: '140px',
      hideMobile: true,
      render: (value) =>
        new Date(value || new Date()).toLocaleDateString(
          t('common.languageCode') === 'ja' ? 'ja-JP' : 'en-US'
        ),
    },
  ];

  const actions: GridAction[] = [
    {
      icon: <Eye size={16} strokeWidth={1.75} />,
      onClick: (item) => navigate(`/organization/warehouses/${item.id}`),
      className: 'grid-action-btn-view',
      title: t('common.view'),
    },
    ...(hasPermission(Permissions.Warehouses.Edit)
      ? [{
          icon: <SquarePen size={16} strokeWidth={1.75} />,
          onClick: (item: any) => navigate(`/organization/warehouses/${item.id}/edit`),
          className: 'grid-action-btn-edit',
          title: t('common.edit'),
        }]
      : []),
    ...(hasPermission(Permissions.Warehouses.Delete)
      ? [{
          icon: <Trash2 size={16} strokeWidth={1.75} />,
          onClick: (item: any) => openDeleteModal(item.id, item.name),
          className: 'grid-action-btn-delete',
          title: t('common.delete'),
        }]
      : []),
  ];

  // ── Render ───────────────────────────────────────────────────────────────

  return (
    <Layout>
      <div className="warehouse-list-page">

        {/* Header */}
        <div className="ims-list-header">
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <div style={{
              width: 40, height: 40, borderRadius: 10,
              background: 'var(--ims-accent-bg)', color: 'var(--ims-accent)',
              display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
            }}>
              <Warehouse size={20} />
            </div>
            <div>
              <h1 className="ims-list-title">{t('warehouse.title')}</h1>
              <p style={{ color: 'var(--ims-text-sub)', fontSize: 13, margin: 0 }}>
                {t('warehouse.subtitle')}
              </p>
            </div>
          </div>
          <div className="ims-list-actions">
            <button className="btn-refresh" onClick={() => loadItems(true)} disabled={refreshing}>
              <RefreshCw size={14} strokeWidth={2} className={refreshing ? 'refresh-icon-spinning' : ''} />
              {refreshing ? t('common.refreshing') : t('common.refresh')}
            </button>
            <PermissionGuard claim={Permissions.Warehouses.Create}>
              <button className="btn-new" onClick={() => navigate('/organization/warehouses/create')}>
                <CirclePlus size={16} />
                {t('warehouse.newWarehouse')}
              </button>
            </PermissionGuard>
          </div>
        </div>

        {/* Stat cards */}
        {!loading && (
          <div className="ims-stats-row" style={{ marginBottom: 16 }}>
            <div className="ims-stat-card">
              <div className="ims-stat-icon wh-icon-total"><Warehouse size={18} /></div>
              <div className="ims-stat-info">
                <span className="ims-stat-value">{totalWarehouses}</span>
                <span className="ims-stat-label">{t('warehouse.totalWarehouses')}</span>
              </div>
            </div>
            <div className="ims-stat-card">
              <div className="ims-stat-icon wh-icon-active"><Warehouse size={18} /></div>
              <div className="ims-stat-info">
                <span className="ims-stat-value">{activeWarehouses}</span>
                <span className="ims-stat-label">{t('warehouse.activeWarehouses')}</span>
              </div>
            </div>
            <div className="ims-stat-card">
              <div className="ims-stat-icon wh-icon-city"><Warehouse size={18} /></div>
              <div className="ims-stat-info">
                <span className="ims-stat-value">{uniqueCities}</span>
                <span className="ims-stat-label">{t('warehouse.cities')}</span>
              </div>
            </div>
          </div>
        )}

        {/* Alerts */}
        {error && (
          <div className="ims-alert ims-alert-error" style={{ marginBottom: 12 }}>
            <span>{error}</span>
            <button
              type="button"
              onClick={() => setError(null)}
              style={{ marginInlineStart: 'auto', background: 'none', border: 'none', cursor: 'pointer', color: 'inherit' }}
            >✕</button>
          </div>
        )}
        {successMessage && (
          <div className="ims-alert ims-alert-success" style={{ marginBottom: 12 }}>
            <span>{successMessage}</span>
            <button
              type="button"
              onClick={() => setSuccessMessage(null)}
              style={{ marginInlineStart: 'auto', background: 'none', border: 'none', cursor: 'pointer', color: 'inherit' }}
            >✕</button>
          </div>
        )}

        {/* Grid */}
        <div className="ims-grid-wrapper">
          <DataGrid
            data={items}
            columns={columns}
            actions={actions}
            loading={loading}
            pageSize={10}
            searchPlaceholder={t('common.search')}
            emptyMessage={t('warehouse.noWarehousesFound')}
            rowsPerPageOptions={[5, 10, 25, 50]}
          />
        </div>

      </div>

      {/* Delete confirmation modal */}
      {showDeleteModal && (
        <div className="ims-modal-overlay">
          <div className="ims-modal" style={{ maxWidth: 420 }}>
            <div style={{ padding: '24px 24px 0' }}>
              <div style={{
                width: 44, height: 44, borderRadius: 10,
                background: 'var(--ims-red-bg)', color: 'var(--ims-red-text)',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                marginBottom: 14,
              }}>
                <Trash2 size={20} />
              </div>
              <h3 style={{ margin: '0 0 8px', fontSize: 17, fontWeight: 700, color: 'var(--ims-text-main)' }}>
                {t('warehouse.deleteTitle')}
              </h3>
              <p style={{ margin: 0, fontSize: 14, color: 'var(--ims-text-sub)', lineHeight: 1.5 }}>
                {t('warehouse.deleteConfirm', { name: deleteName })}
              </p>
            </div>
            <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end', padding: '20px 24px' }}>
              <button className="btn-cancel" onClick={closeDeleteModal} disabled={deleting}>
                {t('common.cancel')}
              </button>
              <button className="btn-delete" onClick={handleDelete} disabled={deleting}>
                {deleting && <span className="ims-btn-spinner" />}
                {deleting ? t('common.deleting') : t('common.delete')}
              </button>
            </div>
          </div>
        </div>
      )}

    </Layout>
  );
};

export default WarehouseListPage;
